<template>
    <q-drawer
        :modelValue="true"
        side="left"
        behavior="desktop"
        :breakpoint="500"
        elevated
        :width="drawerWidth"
        :miniWidth="drawerMiniWidth"
        :mini="!expanded"
        contentClass="Sidebar"
        showIfAbove
    >
        <div class="DrawerContent row no-wrap full-height">
            <div class="Rail column no-wrap">
                <QList class="RailTabs col">
                    <QItem
                        v-ripple
                        clickable
                        class="RailTab RailControl"
                        tag="button"
                        type="button"
                        aria-label="Toggle sidebar"
                        @click="toggleExpanded"
                    >
                        <QItemSection avatar>
                            <QIcon
                                name="menu"
                                size="24px"
                            />
                        </QItemSection>
                    </QItem>

                    <template v-if="(tabs || []).length">
                        <QItem
                            v-for="(tab, index) in tabs"
                            :key="`rail-tab-${index}`"
                            v-ripple
                            clickable
                            :disable="tab.disabled"
                            :active="isTabActive(tab)"
                            activeClass="active"
                            class="RailTab"
                            :class="{ hovered: hoveredKey === tab.id }"
                            tag="button"
                            type="button"
                            :aria-label="tab.label"
                            @click="handleChangeTab(tab.id)"
                            @mouseenter="setHovered(tab.id)"
                            @mouseleave="clearHovered(tab.id)"
                            @focus="setHovered(tab.id)"
                            @blur="clearHovered(tab.id)"
                        >
                            <QItemSection
                                v-if="tab.icon"
                                avatar
                            >
                                <QIcon
                                    :name="tab.icon"
                                    size="24px"
                                />
                            </QItemSection>

                            <q-tooltip
                                anchor="center right"
                                self="center left"
                            >
                                {{ tab.label }}
                            </q-tooltip>
                        </QItem>
                    </template>
                </QList>

                <QList class="RailBottom">
                    <QItem
                        v-ripple
                        clickable
                        :active="isProfileActive"
                        activeClass="active"
                        class="RailTab"
                        :class="{ hovered: hoveredKey === 'profile' }"
                        tag="button"
                        type="button"
                        :aria-label="$t('general.profile')"
                        @click="goToAccount"
                        @mouseenter="setHovered('profile')"
                        @mouseleave="clearHovered('profile')"
                        @focus="setHovered('profile')"
                        @blur="clearHovered('profile')"
                    >
                        <QItemSection avatar>
                            <QIcon
                                name="account_circle"
                                size="24px"
                            />
                        </QItemSection>

                        <q-tooltip
                            anchor="center right"
                            self="center left"
                        >
                            {{ $t('general.profile') }}
                        </q-tooltip>
                    </QItem>

                    <QItem
                        v-ripple
                        clickable
                        class="RailTab"
                        :class="{ hovered: hoveredKey === 'signout' }"
                        tag="button"
                        type="button"
                        :aria-label="$t('general.signout')"
                        @click="signOut"
                        @mouseenter="setHovered('signout')"
                        @mouseleave="clearHovered('signout')"
                        @focus="setHovered('signout')"
                        @blur="clearHovered('signout')"
                    >
                        <QItemSection avatar>
                            <QIcon
                                name="power_settings_new"
                                size="24px"
                            />
                        </QItemSection>

                        <q-tooltip
                            anchor="center right"
                            self="center left"
                        >
                            {{ $t('general.signout') }}
                        </q-tooltip>
                    </QItem>
                </QList>
            </div>

            <div
                v-show="expanded"
                class="Panel column no-wrap col"
            >
                <QList class="PanelTabs col">
                    <QItem
                        class="PanelTab PanelLogo"
                        tag="div"
                    >
                        <QItemSection>
                            <Logo />
                        </QItemSection>
                    </QItem>

                    <template v-if="(tabs || []).length">
                        <template
                            v-for="(tab, index) in tabs"
                            :key="tab.id"
                        >
                            <QItem
                                v-ripple
                                clickable
                                :disable="tab.disabled"
                                :active="isTabActive(tab)"
                                activeClass="active"
                                class="PanelTab"
                                :class="{ hovered: hoveredKey === tab.id }"
                                tag="button"
                                type="button"
                                @click="handleChangeTab(tab.id)"
                                @mouseenter="setHovered(tab.id)"
                                @mouseleave="clearHovered(tab.id)"
                                @focus="setHovered(tab.id)"
                                @blur="clearHovered(tab.id)"
                            >
                                <QItemSection>
                                    <QItemLabel class="PanelTabLabel">
                                        {{ tab.label }}
                                    </QItemLabel>
                                </QItemSection>

                                <QItemSection
                                    v-if="tab.badge"
                                    side
                                >
                                    <QBadge
                                        class="Badge"
                                        :color="tab.badge.color"
                                        :label="tab.badge.label"
                                    />
                                </QItemSection>
                            </QItem>

                            <template v-if="isTabActive(tab) && (tab.children || []).length">
                                <QItem
                                    v-for="(child, cIdx) in tab.children"
                                    :key="`panel-subtab-${index}-${cIdx}`"
                                    v-ripple
                                    clickable
                                    :active="modelValue === child.id"
                                    activeClass="active"
                                    class="PanelTab PanelSubTab"
                                    tag="button"
                                    type="button"
                                    @click="handleChangeTab(child.id)"
                                >
                                    <QItemSection>
                                        <QItemLabel class="PanelTabLabel">
                                            {{ child.label }}
                                        </QItemLabel>
                                    </QItemSection>
                                </QItem>
                            </template>
                        </template>
                    </template>
                </QList>

                <QList class="PanelBottom">
                    <QItem
                        v-ripple
                        clickable
                        :active="isProfileActive"
                        activeClass="active"
                        class="PanelTab"
                        :class="{ hovered: hoveredKey === 'profile' }"
                        tag="button"
                        type="button"
                        @click="goToAccount"
                        @mouseenter="setHovered('profile')"
                        @mouseleave="clearHovered('profile')"
                        @focus="setHovered('profile')"
                        @blur="clearHovered('profile')"
                    >
                        <QItemSection>
                            <QItemLabel class="PanelTabLabel">
                                {{ $t('general.profile') }}
                            </QItemLabel>
                        </QItemSection>
                    </QItem>

                    <QItem
                        v-ripple
                        clickable
                        class="PanelTab"
                        :class="{ hovered: hoveredKey === 'signout' }"
                        tag="button"
                        type="button"
                        @click="signOut"
                        @mouseenter="setHovered('signout')"
                        @mouseleave="clearHovered('signout')"
                        @focus="setHovered('signout')"
                        @blur="clearHovered('signout')"
                    >
                        <QItemSection>
                            <QItemLabel class="PanelTabLabel">
                                {{ $t('general.signout') }}
                            </QItemLabel>
                        </QItemSection>
                    </QItem>
                </QList>
            </div>
        </div>
    </q-drawer>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Logo,
} from '@/components'
import { mediaQueryMixin } from '@/mixins'

export default {
    name: 'SideBar',
    components: {
        Logo,
    },
    mixins: [mediaQueryMixin],
    inject: ['App'],
    props: {
        modelValue: VueTypes.string,
        tabs: VueTypes.array,
    },
    emits: ['update:modelValue'],
    data() {
        return {
            expanded: false,
            drawerWidth: 290,
            drawerMiniWidth: 70,
            hoveredKey: null,
        }
    },
    computed: {
        isProfileActive() {
            const routeName = this.$route?.name
            return routeName === 'Account' || routeName === 'Doc'
        },
    },
    watch: {
        isMobile(value) {
            if (value) this.expanded = false
        },
        '$route.fullPath': {
            handler() {
                // Al navegar con back/forward el foco puede quedarse en el botón anterior,
                // manteniendo el estado visual (hover/focus) como si siguiera activo.
                this.hoveredKey = null
            },
        },
    },
    mounted() {},
    methods: {
        isTabActive(tab) {
            if (!tab) return false
            if (this.modelValue === tab.id) return true
            return (tab.children || []).some((c) => c?.id === this.modelValue)
        },
        handleChangeTab(value) {
            this.$emit('update:modelValue', value)
        },
        setHovered(value) {
            this.hoveredKey = value
        },
        clearHovered(value) {
            if (this.hoveredKey === value) this.hoveredKey = null
        },
        toggleExpanded() {
            this.expanded = !this.expanded
        },
        goToAccount() {
            this.$router.push({ name: 'Account' }).catch(() => {})
        },
        signOut() {
            this.App?.signout?.()
        },
    },
}
</script>

<style scoped lang="scss">
.Sidebar {
    background: $white;
}

.DrawerContent {
    width: 100%;
}

.Rail {
    width: $sidebar-min-width;
    background: $primary;
    color: $white;
}

.RailTabs {
    padding: 0;
}

.RailBottom {
    padding: 0;
}

.RailTab {
    width: 100%;
    height: 64px;
    min-height: 64px;
    padding: 0;
    border: 0;
    background: transparent;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0.9;

    :deep(.q-item__section--avatar) {
        min-width: auto;
        padding: 0;
    }

    .q-icon {
        color: $white;
    }

    &:hover {
        background: rgba($white, 0.12);
    }

    &.hovered {
        background: rgba($white, 0.12);
    }

    &.active {
        opacity: 1;
        background: rgba($white, 0.16);
    }
}

.RailControl {
    height: $header-height;
    min-height: $header-height;
}

.Panel {
    background: $white;
}

.PanelTabs {
    padding: 0;
}

.PanelSubTab {
    padding-left: 24px;
}

.PanelBottom {
    padding: 0;
}

.PanelTab {
    width: 100%;
    height: 64px;
    min-height: 64px;
    padding: 0 22px;
    border: 0;
    background: transparent;
    letter-spacing: .5px;
    outline: none;
    font-weight: 400;
    color: black;
    opacity: 0.7;
    text-align: left;
    display: flex;
    align-items: center;

    &:hover {
        background: rgba($primary, 0.08);
        opacity: 1;
    }

    &.hovered {
        background: rgba($primary, 0.08);
        opacity: 1;
    }

    &.active {
        opacity: 1;
        color: $primary;
        font-weight: 600;
        background: rgba($primary, 0.08);
    }
}

.PanelLogo {
    height: $header-height;
    min-height: $header-height;
    padding: 0;
    opacity: 1;

    &:hover {
        background: transparent;
        opacity: 1;
    }

    :deep(.Logo) {
        width: 100%;
    }
}

.Badge {
    margin-left: 10px;
}

</style>
